/**
 * Graphique d'évolution de la trésorerie sur 3 mois
 * Utilise Recharts AreaChart avec ligne de référence à 0
 */

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import type { MoisTresorerie, ModeAffichageTresorerie } from '@/types/tresorerie';

interface TresorerieChartProps {
  mois: MoisTresorerie[];
  soldeActuel: number;
  mode?: ModeAffichageTresorerie;
}

interface ChartDataPoint {
  name: string;
  solde: number;
  type: 'debut' | 'fin';
}

/**
 * Formate un montant en euros
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Composant graphique trésorerie
 */
export function TresorerieChart({ mois, soldeActuel, mode = 'ttc' }: TresorerieChartProps) {
  const isHt = mode === 'ht';

  // Construire les données pour le graphique
  const data: ChartDataPoint[] = [
    { name: "Aujourd'hui", solde: soldeActuel, type: 'debut' },
    ...mois.map((m) => ({
      name: m.label,
      solde: isHt ? m.solde_fin_ht : m.solde_fin,
      type: 'fin' as const,
    })),
  ];

  // Calculer les bornes du graphique
  const allValues = data.map((d) => d.solde);
  const minSolde = Math.min(...allValues, 0);
  const maxSolde = Math.max(...allValues);
  const padding = Math.abs(maxSolde - minSolde) * 0.1 || 10000;

  // Déterminer la couleur selon si on passe en négatif
  const hasNegative = allValues.some((v) => v < 0);
  const areaColor = hasNegative ? '#ef4444' : '#3b82f6';
  const gradientId = hasNegative ? 'colorSoldeNeg' : 'colorSoldePos';

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={data} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
        <defs>
          <linearGradient id="colorSoldePos" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorSoldeNeg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
          </linearGradient>
        </defs>

        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />

        <XAxis
          dataKey="name"
          tick={{ fontSize: 12 }}
          className="text-muted-foreground"
          tickLine={false}
          axisLine={false}
        />

        <YAxis
          tickFormatter={(value) => {
            if (Math.abs(value) >= 1000000) {
              return `${(value / 1000000).toFixed(1)}M€`;
            }
            if (Math.abs(value) >= 1000) {
              return `${(value / 1000).toFixed(0)}k€`;
            }
            return `${value}€`;
          }}
          domain={[minSolde - padding, maxSolde + padding]}
          tick={{ fontSize: 12 }}
          className="text-muted-foreground"
          tickLine={false}
          axisLine={false}
          width={60}
        />

        <Tooltip
          formatter={(value: number) => [formatCurrency(value), 'Solde']}
          contentStyle={{
            backgroundColor: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
          labelStyle={{ fontWeight: 600 }}
        />

        {/* Ligne de référence à 0 */}
        <ReferenceLine
          y={0}
          stroke="hsl(var(--destructive))"
          strokeDasharray="5 5"
          strokeWidth={2}
          label={{
            value: 'Seuil critique',
            position: 'insideTopRight',
            fill: 'hsl(var(--destructive))',
            fontSize: 11,
          }}
        />

        <Area
          type="monotone"
          dataKey="solde"
          stroke={areaColor}
          fillOpacity={1}
          fill={`url(#${gradientId})`}
          strokeWidth={2}
          dot={{
            fill: areaColor,
            strokeWidth: 2,
            r: 4,
          }}
          activeDot={{
            fill: areaColor,
            strokeWidth: 2,
            r: 6,
          }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
